(() => {
  const CONFIG = window.DC_EXPERIMENTS_CONFIG || {};
  const MIRROR_KEY = "dc_exp";
  const ID_COOKIE = "dc_anonymous_id";

  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  };

  const setCookie = (name, value, days = 365) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  };

  // Durable device id shared with the existing (checkout) integration so a
  // returning visitor keeps the same bucketing unit across surfaces.
  const getAnonymousId = () => {
    let id = getCookie(ID_COOKIE);
    if (!id) {
      id = localStorage.getItem(ID_COOKIE);
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(ID_COOKIE, id);
      }
      setCookie(ID_COOKIE, id);
    }
    return id;
  };

  // Experiments whose treatment must land before first paint. Their resolved
  // params are mirrored to localStorage and stamped as data-exp-<attr> on
  // <html> by experiments-head.liquid, which CSS keys off of.
  const STAMPED_EXPERIMENTS = [
    {
      name: "product_price_color",
      params: [
        { param: "color", attr: "price-color" },
        { param: "bold", attr: "price-bold" },
        { param: "size", attr: "price-size" },
      ],
    },
    {
      name: "announcement_bar",
      params: [{ param: "variant", attr: "banner" }],
    },
  ];

  const MANAGED_ATTRS = STAMPED_EXPERIMENTS.reduce(
    (all, experiment) => all.concat(experiment.params.map((p) => p.attr)),
    []
  );

  // ?dc_exp_<attr>=<value> forces a variant for QA. Layered over the live
  // decision when stamping but never written to the mirror, so an override
  // neither survives the pageview nor gets overwritten by the SDK mid-visit.
  const readOverrides = () => {
    const out = {};
    try {
      new URLSearchParams(location.search).forEach((value, key) => {
        if (key.indexOf("dc_exp_") === 0) {
          out[key.slice(7)] = value;
        }
      });
    } catch (e) {}
    return out;
  };

  const OVERRIDES = readOverrides();

  // Writes every managed attribute on every call, clearing the ones absent from
  // `values`. A visitor who leaves an experiment must lose the attribute they
  // were stamped with at first paint, or they keep the old treatment forever.
  const stamp = (values) => {
    const el = document.documentElement;
    MANAGED_ATTRS.forEach((attr) => {
      const value = values[attr];
      if (value == null) {
        el.removeAttribute(`data-exp-${attr}`);
      } else {
        el.setAttribute(`data-exp-${attr}`, value);
      }
    });
  };

  // SDK-less fallback so callers never throw if Statsig fails to load or the
  // key is unset. Renders control everywhere.
  const noop = {
    ready: Promise.resolve(),
    get: (name, param, fallback) => fallback,
    gate: () => false,
    logEvent: () => {},
  };

  window.theme = window.theme || {};

  if (!window.Statsig || !CONFIG.key) {
    window.theme.experiments = noop;
    return;
  }

  const { StatsigClient } = window.Statsig;
  const host = window.location.host;
  const tier =
    host === "store.dandelionchocolate.com" ||
    host === "www.dandelionchocolate.com" ||
    host === "dandelionchocolate.com"
      ? "production"
      : "development";

  const anonymousId = getAnonymousId();

  const client = new StatsigClient(
    CONFIG.key,
    { customIDs: { stableID: anonymousId } },
    { environment: { tier } }
  );

  // Reads each stamped experiment (logging one exposure for the variant that
  // was actually rendered) and rewrites the mirror from scratch, so params the
  // visitor is no longer enrolled in drop out of the cache as well as the DOM.
  const refresh = () => {
    const mirror = {};
    STAMPED_EXPERIMENTS.forEach((experiment) => {
      const exp = client.getExperiment(experiment.name);
      experiment.params.forEach(({ param, attr }) => {
        const value = exp.get(param, null);
        if (value != null) {
          mirror[attr] = String(value);
        }
      });
    });
    try {
      localStorage.setItem(MIRROR_KEY, JSON.stringify(mirror));
    } catch (e) {}
    stamp(Object.assign({}, mirror, OVERRIDES));
  };

  const ready = client.initializeAsync().then(refresh);

  // cartAPI.addItem is the single funnel every add-to-cart path goes through,
  // and emits this once per add. experiments.js is deferred from <head> and so
  // runs before cart-core.js, meaning no add can slip past the listener.
  let idStamped = false;

  document.addEventListener("cart:item-added", (event) => {
    const product = event.detail && event.detail.product;
    if (!product) return;

    // Carry the bucketing id onto the cart so it reaches the order, where the
    // checkout web pixel reads it back: that sandbox can see neither this
    // page's cookie nor its localStorage.
    //
    // detail.cart is a forced re-fetch from the add, so its attributes are
    // current and the already-stamped case costs no request at all. idStamped
    // covers the gap the cart can't: setAttributes is debounced, so a second
    // add landing inside that window still sees an unstamped cart.
    // dc_env rides along because checkout always runs on the shop domain: the
    // pixel can't tell a localhost session from a real one, so the storefront
    // has to tell it which tier to log the purchase against.
    const cart = event.detail && event.detail.cart;
    const attrs = (cart && cart.attributes) || {};
    const stamped = attrs.dc_anon === anonymousId && attrs.dc_env === tier;
    if (!idStamped && !stamped && window.theme.cartAPI) {
      idStamped = true;
      window.theme.cartAPI.setAttributes({
        attributes: { dc_anon: anonymousId, dc_env: tier },
      });
    }

    ready.then(() => {
      client.logEvent("add_to_cart", (product.price || 0) / 100, {
        variant_id: String(product.id || ""),
        sku: String(product.sku || ""),
        product_title: String(product.product_title || product.title || ""),
        quantity: String(product.quantity || 1),
      });
    });
  });

  window.theme.experiments = {
    ready,
    get: (name, param, fallback) => client.getExperiment(name).get(param, fallback),
    gate: (name) => client.checkGate(name),
    logEvent: (name, value, meta) => client.logEvent(name, value, meta),
  };
})();
